import numeral, { bigDiv, bigRound, roundAndFormat } from "c8-numeraljs";
import { DEFAULT_LOCALE, getCurrencyCodeForLocale } from "csi/utility/currencyUtility";
import _ from "lodash";

// constants
const DEFAULT_CURRENCY = "$";
export const DEFAULT_DECIMALS: number = 4;
export const DEFAULT_DECIMAL_FORMAT = "#,##0.####";
export const DEFAULT_INTEGER_FORMAT = "#,##0";
export const DECIMAL_INTERNAL: string = ".";

const EMPTY_STRING = "";
const FRACTION_MIN: number[] = [0, 1];
const FRACTION_MAX: number[] = [1, 0];
const FORMAT_IN_FRACTION = "IN - Fraction";
const GROUP_INTERNAL: string = ",";
const VALUE_FORMAT_CORRECTION_FOR_NUMERALJS = "$1.$2[$3]$4";

// miscellaneous RegExp
const RE_DECIMAL_INTERNAL = /\./g;
const RE_FORMAT_CORRECTION_FOR_NUMERALJS = /(^.*)\.(.*?)(#+)(.*)/;
const RE_FRACTION: RegExp = /^-?\+?\s*(\d+\s+)?\d+\s*\/\s*\d+/;
const RE_GROUP_INTERNAL = /,/g;
const RE_NUMBER_SPECIAL = /,|^\$|E[+-].*/g;
const RE_PERCENT = /%/g;

// String: characters used for decimal and groups
let _numberDecimal: string;
let _numberGroup: string;

// RegExp: regular expressions used to manipulate number strings
let _reNumberDecimal: RegExp = null;
let _reNumberGroup: RegExp = null;

// Boolean: indicates if using default number formatting
let _numberDefaultNumberLocale: boolean;

// Object: mapping of formats to the number of decimals in that format
const _numberMapFormatDecimals: Record<string, number> = {};

// orignal number format - [ numeralFormat-Read, numeralFormat-Write, currencySymbol]
type FormatProperties = [numeralReadFormat: string, numeralWriteFormat: string, currencySymbol: string];
const _registeredFormatProperties: Record<string, FormatProperties> = {};

// keep the registered numeral locale
const _registeredNumberLocale: Record<string, string> = {};

/**
 * Old Function in types.js : getNumberRead
 * get value for number used for display as HTML text
 */
export function formatNumber(value: number, format?: string): string {
	if (value == null) return "";

	if (isNaN(value)) return value ? value.toString() : "";

	let v: string;
	if (format == FORMAT_IN_FRACTION) {
		// convert to fraction
		v = getFractionString(value, true);
	} else if (format) {
		v = numeralNumberFormat(value, getFormatProperties(format)[0], format);
	} else {
		// restrict to DEFAULT_DECIMALS digits after the decimal point
		v = String(numberRound(value, DEFAULT_DECIMALS));
	}
	return v;
}

/**
 * format the value based on the type, pattern, locale - used in the format() in Rollup
 * serverside format method use the  java.text.MessageFormat so this method added to support format, currency, percent
 */
export function formatNumberWithStylePattern(value: number, locale: string, pattern: string, type: string): string {
	if (value == null) {
		return "";
	}

	if (isNaN(value)) {
		return value ? value.toString() : "";
	}

	if (type == null) {
		return numeralNumberFormat(value, getFormatProperties(pattern)[0], pattern);
	} else {
		locale = locale || DEFAULT_LOCALE;
		if (type == "integer") {
			return new Intl.NumberFormat(locale, {
				style: "decimal",
			})
				.format(value);
		}
		else if (type == "currency") {
			return new Intl.NumberFormat(locale, {
				style: "currency",
				currency: getCurrencyCodeForLocale(locale),
			})
				.format(value);
		} else if (type == "percent") {
			return new Intl.NumberFormat(locale, {
				style: "percent",
			})
				.format(value);
		}
	}
}

/**
 * get string value for number used for editing in input control
 */
export function formatNumberForEditing(value: number, format: string): string {
	//TODO : Add the condition for checking the number is null, undefined or NaN and
	//return the appropriate values same as for formatNumber

	let s: string;
	if (format == FORMAT_IN_FRACTION) {
		// convert to fraction
		s = getFractionString(value, false);
	} else if (format) {
		s = numeralNumberFormat(value, getFormatProperties(format)[1], format);

		if (!s)
			s = format;
	} else {
		s = String(value);
	}

	// remove percentage sign
	return (!s) ? EMPTY_STRING : s.toString()
		.replace(RE_PERCENT, EMPTY_STRING);
}

function getFormatProperties(format: string): FormatProperties {
	if (_registeredFormatProperties[format]) {
		return _registeredFormatProperties[format];
	} else {
		// numeral read format
		let readFormat = formatReadNumberFormat(format);
		// numeral write format
		let writeFormat = formatWriteNumberFormat(format);
		// currency symbol
		let currencySymbol = getCurrencySymbol(format);
		if (currencySymbol === "") {
			currencySymbol = DEFAULT_CURRENCY;
		}
		// remove the currency symbol from writeFormat
		writeFormat = writeFormat.replace(currencySymbol, EMPTY_STRING)
			.trim();
		// replace currency if not '$'
		if (currencySymbol !== "" && currencySymbol !== DEFAULT_CURRENCY) {
			readFormat = readFormat.replace(currencySymbol, DEFAULT_CURRENCY);
			writeFormat = writeFormat.replace(currencySymbol, DEFAULT_CURRENCY);
		}
		return _registeredFormatProperties[format] = [readFormat, writeFormat, currencySymbol];
	}
}

/**
 * Format the number format for read and also cache the format for faster processing
 */
function formatReadNumberFormat(format: string): string {
	return format.replace(RE_FORMAT_CORRECTION_FOR_NUMERALJS, VALUE_FORMAT_CORRECTION_FOR_NUMERALJS);
}

/**
 * Format the number format for write and also cache the format for faster processing
 */
function formatWriteNumberFormat(format: string): string {
	// remove scientific notation, grouping separator and all non-special formatting characters (;,#,?,0)
	return format.replace(RE_NUMBER_SPECIAL, EMPTY_STRING)
		.replace(RE_FORMAT_CORRECTION_FOR_NUMERALJS, VALUE_FORMAT_CORRECTION_FOR_NUMERALJS);
}

/**
 *  replace all numbers, spaces, commas, and periods, %, [, ] with an empty string, so we should only be left with currency symbol
 */
export function getCurrencySymbol(format: string): string {
	return format?.replace(/[\d\.,#%\[\] ]/g, "") ?? "";
}

/**
 * Get the default locale properties for number based on the locale, if locale is null, it refer the browser locale
 * Example: if property= "decimal" - it will return the decimal separator of the locale
 *          if property= "group" - it will return the group separator of the locale
 */
function getDefaultLocaleNumberProperty(property: string, locale?: string): string {
	if (locale) {
		return Intl.NumberFormat(locale)
			.formatToParts(1000.1)
			.find((part) => part.type == property).value;
	} else {
		return Intl.NumberFormat(Intl.NumberFormat()
			.resolvedOptions().locale)
			.formatToParts(1000.1)
			.find((part) => part.type == property).value;
	}
}

/**
 * Given floating point value, find fraction in Stern-Brocot tree represented as array of numerator and denominator
 */
export function getFraction(value: number): number[] | null {
	if (value == null) {
		return null;
	}

	if (value < 0) {
		throw new Error("getFraction: value must be positive - " + value);
	}

	if (value === 0) {
		return FRACTION_MIN;
	} else if (value === Infinity) {
		return FRACTION_MAX;
	}

	let frac1: number[];
	let frac2: number[];

	if (value < 1) {
		frac1 = FRACTION_MIN;
		frac2 = [1, 1];
	} else {
		const lowerValue = Math.floor(value);
		if (lowerValue === value) {
			return [value, 1];
		}

		const upperValue = Math.ceil(value);
		frac1 = [lowerValue, 1];
		frac2 = [upperValue, 1];
	}

	let f: number[];
	let valueNew: number;
	let i = 0;

	while (true) {
		f = getIntermediateFraction(frac1, frac2);
		valueNew = getFractionValue(f);

		if (value === valueNew) {
			break;
		} else if (value < valueNew) {
			frac2 = f;
		} else {
			frac1 = f;
		}

		++i;
	}

	return f;
}

/**
 * Return floating point value for fraction
 */
export function getFractionValue(frac: number[]): number {
	return frac[0] / frac[1];
}

/**
 * get fraction up to denominator 16
 */
export function getFractionString(value: number, bHtml: boolean): string {
	value = numberTruncate(value, 0.0001);
	if (!value) return "0";

	const whole = (value > 0) ? Math.floor(value) : Math.ceil(value);
	const dec = Math.abs(value - whole);
	if (dec == 0) return String(whole);

	let rem = 1;
	let den: number;
	let num: number;

	function getClosestRemainder(denom): void {
		const x = Math.round(denom * dec);
		const y = Math.abs((x / denom) - dec);

		if (y < rem) {
			den = denom;
			num = x;
			rem = y;
		}
	}

	getClosestRemainder(16);
	getClosestRemainder(10);
	getClosestRemainder(3);

	if (num == 0) return String(whole);
	if (num == den) return String(whole + ((value < 0) ? -1 : 1));

	const gcd = greatestCommonDenominator(num, den);
	num /= gcd;
	den /= gcd;

	const s = whole ? `${whole} ` : ((value < 0) ? "-" : "");
	return bHtml ? `<span>${s}<sup>${num}</sup>&frasl;<sub>${den}</sub></span>` : `${s}${num}/${den}`;
}

/**
 * Find intermediate fraction given two surrounding fractions;
 * reference https://www.cut-the-knot.org/blue/Stern.shtml
 */
export function getIntermediateFraction(frac1: number[], frac2: number[]): number[] {
	frac1 = frac1 || FRACTION_MIN;
	frac2 = frac2 || FRACTION_MAX;
	const num = frac1[0] + frac2[0];
	const den = frac1[1] + frac2[1];
	const gcd = greatestCommonDenominator(num, den);
	return [num / gcd, den / gcd];
}

/**
 * Value might be string, as it might be in format of fractions: like "1 2/3" or "1/2 or it might be normal number
 * Return value is always of type Number
 */
export function getInternalNumber(value: number | string): number {
	const stringValue = value?.toString() || "";
	value = stringValue.trim()
		.replace(_reNumberGroup, EMPTY_STRING);

	if (!_numberDefaultNumberLocale) {
		value = value.replace(_reNumberDecimal, DECIMAL_INTERNAL);
	}

	if (stringValue.match(RE_FRACTION)) {
		// handle fractions
		const a = stringValue.split("/");
		const left = _.filter(a[0].split(" "), (item) => item !== "");
		const index = left.length - 1;
		const den = parseInt(a[1].replace(/ /g, ""));
		const num = parseInt(left[index]);
		const frac = num / den;

		if (index > 0) {
			// if only negative sign, then just negate fraction
			let whole: string | number = (index > 1) ? left[0] + left[1] : left[0];

			if (whole == "-") {
				value = -frac;
			} else {
				whole = parseInt(whole);
				value = (whole < 0) ? (whole - frac) : (whole + frac);
			}
		} else {
			value = frac;
		}
	}
	return Number(value);
}

/**
 * return character used to represent decimal in current locale
 */
export function getNumberDecimal(): string {
	return _numberDecimal;
}

/**
 * return character used to represent thousand separator in current locale
 */
export function getNumberGroup(): string {
	return _numberGroup;
}

/**
 * Given a floating-point value and its format, return a pair of numbers indicating the number range represented by that single value
 */
export function getNumberRange(format: string, value: number): number[] {
	var n = numberOfDecimalsInFormat(format);
	var base = numberRound(value, n++);
	var delta = Number("5e-" + n);
	return [numberRound(base - delta, n), numberRound(base + delta, n)];
}

/**
 * If format is percentage, then return value as percentage by dividing by 100; also round off so no floating-point errors,
 * such as 1.45 becoming 0.1499999999. If format is not percentage, then return null.
 */
export function getPercentageNumber(value: number, format: string): number | null {
	if (format && (format.indexOf("%") >= 0)) {
		return bigRound(bigDiv(value, 100.0), numberOfDecimalsInFormat(format));
	}
	return null;
}

/**
 * Return greatest common denominator of two values
 */
export function greatestCommonDenominator(a: number, b: number): number {
	return b ? greatestCommonDenominator(b, a % b) : a;
}

/**
 * Return true if format contains a currency symbol
 */
export function isCurrencyFormat(format: string): boolean {
	return !!getCurrencySymbol(format);
}

/**
 * Return true if format is for a percentage
 */
export function isPercentFormat(format: string): boolean {
	return RE_PERCENT.test(format);
}

/**
 * Return intermediate sort value given surrounding sort values
 */
export function numberGetIntermediateSortValue(v1: number | null, v2: number | null): number {
	const frac1 = getFraction(v1);
	const frac2 = getFraction(v2);
	const f = getIntermediateFraction(frac1, frac2);
	return getFractionValue(f);
}

/**
 * Return localized number format
 */
export function numberGetLocalizedFormat(format: string): string {
	const large = format.indexOf(",") > -1;
	let seed = large ? 1234.5678 : 1.2345678;

	if (format.indexOf(".0") > -1) {
		seed = Math.floor(seed);
	}

	if (format.indexOf("%") > -1) {
		seed /= 100;
	}
	return formatNumber(seed, format);
}

/**
 * Determine number of decimals in format. Percentages are special and require 2 extra decimals since values of 0-1 map to 0-100%.
 */
export function numberOfDecimalsInFormat(format: string): number {
	if (!format) {
		return DEFAULT_DECIMALS;
	}

	let n = _numberMapFormatDecimals[format];
	if (n == null) {
		const bPercent = (format.indexOf("%") >= 0);
		const s = format.split(DECIMAL_INTERNAL)[1];
		if (s == null) {
			n = bPercent ? 2 : 0;
		} else if (bPercent) {
			// add 1 which is combination of 2 decimals for percent minus 1 to account for the percent sign in string
			n = s.length + 1;
		} else {
			n = s.length;
		}
		_numberMapFormatDecimals[format] = n;
	}
	return n;
}

/**
 * Javascript rounding is inconsistent, so use technique below. Reference: http://www.jacklmoore.com/notes/rounding-in-javascript/
 */
export function numberRound(value: number, decimals: number): number {
	const n = Number(value + "e" + decimals);

	// if value was already in exponential form (which means very small or very big), then just use old technique of rounding
	// which in most cases will just result in 0 for numbers such as 1.234567E-10.
	if (isNaN(n)) {
		return Number(value.toFixed(decimals));
	}

	return Number(Math.round(n) + "e-" + decimals);
}

export function numberTruncate(x: number, pre: number): number {
	pre = pre || 1;
	return Math.floor((1. / pre) * x + 0.5) * pre;
}

/**
 * Format the number using the numeraljs
 */
function numeralNumberFormat(v: number, f: string, orginalFormat: string): string {
	try {
		const d = numberOfDecimalsInFormat(f);
		registerNumberLocale(_numberDecimal, _numberGroup, getFormatProperties(orginalFormat)[2]);
		return roundAndFormat(v, f, d);
	} catch (ex) {
		// we currently use Format="Integer" to perform validation, but this has no real meaning here and
		// Dojo throws an exception
		return String(v);
	}
}

/**
 * Register the number locale from decimal, group for number formatting and currency
 */
function registerNumberLocale(decimalSeparator: string, groupSeparator: string, currencySymbol: string): void {
	const localeName: string = "C8-" + decimalSeparator + "-" + groupSeparator + "-" + currencySymbol;
	if (_registeredNumberLocale[localeName]) {
		// locale is already created so only set the locale
		numeral.locale(localeName);
	} else {
		// create the locale and set the locale
		const registeredLocale: string = numeral.register("locale", localeName, {
			// resolves error http://numeraljs.com/ "cannot read property 'abbreviations' of undefined"
			abbreviations: {
				thousand: "k",
				million: "m",
				billion: "b",
				trillion: "t",
			},
			delimiters: {
				thousands: _numberGroup,
				decimal: _numberDecimal,
			},
			currency: {
				symbol: currencySymbol,
			},
		});
		// set the locale
		numeral.locale(localeName);
		_registeredNumberLocale[localeName] = registeredLocale;
	}
}

/**
 * Register the number locale properties - decimal, group for number formatting and currency
 */
export function registerNumberLocaleProperties(decimalSeparator: string, groupSeparator: string, locale?: string, currencySymbol?: string): void {
	// initialize number properties
	_numberDecimal = decimalSeparator || getDefaultLocaleNumberProperty("decimal", locale) || DECIMAL_INTERNAL;
	_numberGroup = groupSeparator || getDefaultLocaleNumberProperty("group", locale) || GROUP_INTERNAL;
	const _currencySymbol = currencySymbol || DEFAULT_CURRENCY;
	// replace space with regular expression to validate the space correctly
	if (!_numberGroup.replace(/\s/g, "")) {
		_reNumberGroup = /\s/g;
	} else {
		_reNumberGroup = (_numberGroup == GROUP_INTERNAL) ? RE_GROUP_INTERNAL : ((_numberGroup == DECIMAL_INTERNAL) ? RE_DECIMAL_INTERNAL : new RegExp(_numberGroup, "g"));
	}

	_numberDefaultNumberLocale = (_numberDecimal == DECIMAL_INTERNAL) && (_numberGroup == GROUP_INTERNAL);
	_reNumberDecimal = (_numberDecimal == DECIMAL_INTERNAL) ? RE_DECIMAL_INTERNAL : ((_numberDecimal == GROUP_INTERNAL) ? RE_GROUP_INTERNAL : new RegExp(_numberDecimal, "g"));
	registerNumberLocale(_numberDecimal, _numberGroup, _currencySymbol);
}

/**
 * Inspired by: https://stackoverflow.com/questions/9461621/format-a-number-as-2-5k-if-a-thousand-or-more-otherwise-900
 * Perform numbers scaling aka abbreviation
 * Example:
 *  12345 => 12.3K
 *  123456789 => 123M
 */
const SI_SYMBOL = ["", "k", "M", "G", "T", "P", "E"];
export function abbreviateNumber(number: number, decimals: number = 1): string | number {
	// what tier? (determines SI symbol)
	var tier = Math.log10(Math.abs(number)) / 3 | 0;

	// if zero, we don't need a suffix
	if (tier == 0) return number;

	// get suffix and determine scale
	var suffix = SI_SYMBOL[tier];
	var scale = Math.pow(10, tier * 3);

	// scale the number
	var scaled = number / scale;

	// format number and add suffix
	return scaled.toFixed(decimals) + suffix;
}


interface GetFixedDecimalPlacesProps {
	format: string;
	decimalSeparator: string;
	prefix: string;
	suffix: string;
}

/**
 * Checks if the format string requires fixed decimal places after the decimal separator.
 * @param format - The format string (e.g., "#0.####" or "#0.0000").
 * @param decimalSeparator - The character used as the decimal separator.
 * @param prefix - The prefix to exclude from the format string.
 * @param suffix - The suffix to exclude from the format string.
 * @returns `true` if there are mandatory zeros after the decimal separator; otherwise, `false`.
 */
export  function isDecimalAllZero({ format, decimalSeparator, prefix, suffix }: GetFixedDecimalPlacesProps): boolean {
	// Remove prefix and suffix from the format string
	const cleanedFormat = format.replace(prefix, "").replace(suffix, "");

	// Find the index of the decimal separator in the format string
	const separatorIndex = cleanedFormat.indexOf(decimalSeparator);

	// If no decimal separator is found, there are no mandatory decimal places
	if (separatorIndex === -1) return false;

	// Check the substring after the decimal separator
	const decimalPart = cleanedFormat.slice(separatorIndex + 1);

	// Return true if the decimal part contains only '0's (mandatory decimal places)
	return decimalPart.length > 0 && /^[0]+$/.test(decimalPart);
}

interface CountDecimalPlacesProps {
	format: string;
	decimalSeparator: string;
	prefix: string;
	suffix: string;
}

/**
 * Counts the maximum decimal places in a format string with prefix and suffix.
 * @param format - The format string (e.g., "$#0.00###USD").
 * @param decimalSeparator - The character used as the decimal separator.
 * @param prefix - The prefix to exclude from the format string.
 * @param suffix - The suffix to exclude from the format string.
 * @returns The maximum decimal places allowed in the format.
 */
export function countDecimalPlaces({ format, decimalSeparator, prefix, suffix }:CountDecimalPlacesProps): number {
	// Remove prefix and suffix from the format string
	const cleanedFormat = format.replace(prefix, "").replace(suffix, "");

	// Find the index of the decimal separator in the cleaned format
	const separatorIndex = cleanedFormat.indexOf(decimalSeparator);

	// If no decimal separator is found, return 0 as there are no decimal places
	if (separatorIndex === -1) return 0;

	// Get the decimal part and count `0`s and `#`s
	const decimalPart = cleanedFormat.slice(separatorIndex + 1);

	return (decimalPart.match(/[0#]/g) || []).length;
}
